import React, { useEffect, useMemo, useRef, useState } from "react";
import { Link } from "react-router-dom";
import { ArrowLeft, ArrowRight, Lock } from "lucide-react";
import Breadcrumb from "./PageComponent/Breadcrum";
import FilterHeader from "./PageComponent/FilterHeader";
import UpgradePopup from "./PageComponent/UpgradePopup";

const ITEMS_PER_PAGE = 10;
const MAX_PAGES_TO_SHOW = 7;
const LOCKED_TITLE = "Premium question";

const getPracticePath = (heading = "") =>
  heading.toLowerCase().replace(/\s/g, "");

const hasUpgradeOnlyRole = (role) => {
  if (Array.isArray(role)) {
    return role.includes(7) || role.includes("7");
  }

  return Number(role) === 7;
};

const PageButton = ({ children, active = false, disabled = false, onClick }) => (
  <button
    type="button"
    disabled={disabled}
    onClick={onClick}
    className={`inline-flex min-h-9 min-w-9 items-center justify-center rounded-md border px-2.5 text-sm font-semibold transition sm:px-3 ${
      active
        ? "border-transparent bg-gradient-to-r from-fuchsia-900 to-fuchsia-700 text-white shadow-sm"
        : "border-slate-200 bg-white text-gray-700 hover:border-purple-200 hover:bg-purple-50 hover:text-purple-700"
    } disabled:cursor-not-allowed disabled:opacity-45`}
  >
    {children}
  </button>
);

const StatusPill = ({ children, className = "" }) => (
  <span
    className={`inline-flex min-h-8 w-fit items-center rounded-md px-3 py-1 text-xs font-bold sm:text-sm ${className}`}
  >
    {children}
  </span>
);

function QuestionRow({
  title,
  number,
  isLocked,
  isAttempted,
  questionId,
  sectionPath,
}) {
  const displayTitle = isLocked ? LOCKED_TITLE : title;
  const attemptedLabel = isAttempted ? "Attempted" : "Not Attempted";

  return (
    <article
      className={`rounded-md border border-slate-200 bg-white p-3 shadow-sm transition hover:border-purple-100 hover:shadow-md sm:p-4 ${
        isLocked ? "select-none" : ""
      }`}
    >
      <div className="grid gap-3 xl:grid-cols-[auto,minmax(12rem,1fr),auto,auto,auto] xl:items-center">
        <div className="flex min-w-0 items-center gap-3">
          <span className="grid h-10 w-10 shrink-0 place-items-center rounded-md bg-slate-100 text-sm font-extrabold text-gray-700 sm:h-11 sm:w-11">
            {number}
          </span>

          <h2
            className={`min-w-0 truncate text-base font-bold text-gray-800 sm:text-lg xl:hidden ${
              isLocked ? "blur-sm" : ""
            }`}
          >
            {displayTitle}
          </h2>
        </div>

        <h2
          className={`hidden min-w-0 truncate text-base font-bold text-gray-800 sm:text-lg xl:block ${
            isLocked ? "blur-sm" : ""
          }`}
        >
          {displayTitle}
        </h2>

        <div className="flex flex-wrap items-center gap-2 xl:contents">
          <StatusPill
            className={
              isLocked
                ? "bg-gray-200 text-gray-500"
                : "bg-gradient-to-r from-fuchsia-900 to-fuchsia-700 text-white"
            }
          >
            {isLocked ? "Locked" : "Medium"}
          </StatusPill>

          <StatusPill
            className={
              isAttempted
                ? "bg-green-50 text-green-700 ring-1 ring-green-100"
                : "bg-slate-50 text-gray-500 ring-1 ring-slate-200"
            }
          >
            {attemptedLabel}
          </StatusPill>
        </div>

        <div className="xl:justify-self-end">
          {isLocked ? (
            <span className="inline-flex min-h-10 w-full items-center justify-center gap-2 rounded-md border border-slate-200 bg-slate-50 px-4 text-sm font-semibold text-gray-400 sm:w-auto">
              <Lock className="h-4 w-4" aria-hidden="true" />
              Locked
            </span>
          ) : (
            <Link
              className="inline-flex min-h-10 w-full items-center justify-center rounded-md bg-white px-4 text-sm font-bold text-gray-800 underline transition hover:text-fuchsia-800 sm:w-auto"
              to={`/practice/${sectionPath}/${encodeURIComponent(questionId)}`}
            >
              Open Question
            </Link>
          )}
        </div>
      </div>
    </article>
  );
}

export default function PageNavigation(props) {
  const qData = props.id || [];
  const heading = props.heading || "Questions";
  const sectionPath = getPracticePath(heading);
  const [userRole, setUserRole] = useState(null);
  const [showPopup, setShowPopup] = useState(false);
  const [currentPage, setCurrentPage] = useState(1);
  const lastScrollTop = useRef(0);

  useEffect(() => {
    const fetchUserRole = async () => {
      try {
        const response = await fetch("/api/check-auth");
        const data = await response.json();

        if (data.success) {
          setUserRole(data.roles);
        }
      } catch (error) {
        console.error("Error checking user role:", error);
      }
    };

    fetchUserRole();
  }, []);

  useEffect(() => {
    const handleScroll = () => {
      const scrollTop =
        window.pageYOffset ||
        document.documentElement.scrollTop ||
        document.body.scrollTop ||
        0;

      if (scrollTop > 80 && scrollTop >= lastScrollTop.current) {
        setShowPopup(true);
      } else if (scrollTop < 40) {
        setShowPopup(false);
      }

      lastScrollTop.current = scrollTop <= 0 ? 0 : scrollTop;
    };

    window.addEventListener("scroll", handleScroll);
    handleScroll();

    return () => {
      window.removeEventListener("scroll", handleScroll);
    };
  }, []);

  const totalPages = Math.max(1, Math.ceil(qData.length / ITEMS_PER_PAGE));
  const startIndex = (currentPage - 1) * ITEMS_PER_PAGE;
  const endIndex = Math.min(startIndex + ITEMS_PER_PAGE, qData.length);
  const visibleQuestions = qData.slice(startIndex, endIndex);
  const halfMaxPages = Math.floor(MAX_PAGES_TO_SHOW / 2);
  const shouldShowUpgradePrompt = hasUpgradeOnlyRole(userRole);
  const hasLockedQuestions = shouldShowUpgradePrompt && qData.length > 5;

  useEffect(() => {
    if (hasLockedQuestions) {
      setShowPopup(true);
    }
  }, [hasLockedQuestions]);

  const pageNumbers = useMemo(() => {
    const numbers = [];

    if (totalPages <= MAX_PAGES_TO_SHOW) {
      for (let page = 1; page <= totalPages; page += 1) numbers.push(page);
    } else if (currentPage <= halfMaxPages) {
      for (let page = 1; page <= MAX_PAGES_TO_SHOW; page += 1) {
        numbers.push(page);
      }
    } else if (currentPage > totalPages - halfMaxPages) {
      for (
        let page = totalPages - MAX_PAGES_TO_SHOW + 1;
        page <= totalPages;
        page += 1
      ) {
        numbers.push(page);
      }
    } else {
      for (
        let page = currentPage - halfMaxPages;
        page <= currentPage + halfMaxPages;
        page += 1
      ) {
        numbers.push(page);
      }
    }

    return numbers;
  }, [currentPage, halfMaxPages, totalPages]);

  const handlePageClick = (page) => {
    if (shouldShowUpgradePrompt) return;
    setCurrentPage(page);
  };

  return (
    <main className="min-h-screen bg-slate-50 pt-24 sm:pl-72">
      <section className="w-full overflow-hidden px-4 pb-10 sm:px-6 lg:px-8">
        <div className="rounded-md bg-gray-100 p-3 sm:p-5 lg:p-6">
        <div className="rounded-md border border-slate-200 bg-white p-4 shadow-sm sm:p-6">
          <div className="flex flex-col gap-4 lg:flex-row lg:items-start lg:justify-between">
            <div>
              <p className="text-sm font-semibold uppercase text-purple-700">
                Practice Questions
              </p>
              <h1 className="mt-1 text-2xl font-extrabold text-gray-900 sm:text-3xl">
                {heading}
              </h1>
            </div>

            <div className="grid grid-cols-2 gap-3 sm:flex sm:items-center">
              <div className="rounded-md bg-slate-50 px-3 py-3 sm:px-4">
                <p className="text-xs font-semibold uppercase text-gray-500">
                  Questions
                </p>
                <p className="text-xl font-extrabold text-gray-900">
                  {qData.length}
                </p>
              </div>
              <div className="rounded-md bg-slate-50 px-3 py-3 sm:px-4">
                <p className="text-xs font-semibold uppercase text-gray-500">
                  Page
                </p>
                <p className="text-xl font-extrabold text-gray-900">
                  {currentPage}/{totalPages}
                </p>
              </div>
            </div>
          </div>

          <div className="mt-5">
            <Breadcrumb />
          </div>

          <div className="mt-5">
            <FilterHeader />
          </div>
        </div>

        <section className="mt-6 space-y-3">
          {visibleQuestions.map((item, index) => (
            <QuestionRow
              key={item._id}
              number={startIndex + index + 1}
              title={item.q_title}
              questionId={item._id}
              sectionPath={sectionPath}
              isAttempted={Boolean(item.isAttempted || item.attempted)}
              isLocked={shouldShowUpgradePrompt && startIndex + index >= 5}
            />
          ))}

          {visibleQuestions.length === 0 && (
            <div className="rounded-md border border-slate-200 bg-white p-8 text-center shadow-sm">
              <p className="text-sm font-semibold uppercase text-purple-700">
                No questions
              </p>
              <p className="mt-2 text-gray-500">
                Questions will appear here when they are available.
              </p>
            </div>
          )}
        </section>

        <nav
          aria-label="Pagination"
          className={`mt-6 rounded-md border border-slate-200 bg-white p-3 shadow-sm ${
            shouldShowUpgradePrompt ? "blur-sm" : ""
          }`}
        >
          <ol className="flex flex-wrap justify-center gap-2">
            <li>
              <PageButton
                disabled={currentPage === 1 || shouldShowUpgradePrompt}
                onClick={() => handlePageClick(Math.max(currentPage - 1, 1))}
              >
                <ArrowLeft className="mr-1 h-4 w-4" aria-hidden="true" />
                Prev
              </PageButton>
            </li>

            {pageNumbers.map((number) => (
              <li key={number}>
                <PageButton
                  active={number === currentPage}
                  disabled={shouldShowUpgradePrompt}
                  onClick={() => handlePageClick(number)}
                >
                  {number}
                </PageButton>
              </li>
            ))}

            <li>
              <PageButton
                disabled={currentPage === totalPages || shouldShowUpgradePrompt}
                onClick={() =>
                  handlePageClick(Math.min(currentPage + 1, totalPages))
                }
              >
                Next
                <ArrowRight className="ml-1 h-4 w-4" aria-hidden="true" />
              </PageButton>
            </li>
          </ol>
        </nav>
        </div>
      </section>

      {showPopup && shouldShowUpgradePrompt && <UpgradePopup />}
    </main>
  );
}
